import React, { useState } from 'react';
import styled from 'styled-components';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AIAssistantVideo from '../../assets/AI assistent.mp4';
import OnboardingVideo from '../../assets/Aivoria AA (Onboarding video).mp4';
import AssistantAgendaVideo from '../../assets/Assitent agenda.mp4';
import AutomatiseringenVideo from '../../assets/Automatiseringen.mp4';
import SkillsVideo from '../../assets/Skills.mp4';

const GuideContainer = styled.div`
  width: 100%;
  padding: 2.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  box-sizing: border-box;
  transition: margin-left 0.3s ease;
  background: #ffffff;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-left: 0;
  }

  @media (min-height: 850px) {
    margin-left: ${props => props.$isSidebarOpen ? '17.5rem' : '3.75rem'};
    width: auto;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  background: black;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  background: #000;
  overflow: hidden;

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoInfo = styled.div`
  padding: 1.5rem;
`;

const VideoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const VideoDescription = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.6;
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isPlaying ? '0' : '1'};
  transition: opacity 0.3s ease;
  pointer-events: none;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 20px solid #667eea;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    margin-left: 4px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  background: #000;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const ModalVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
`;

function VideoGuide() {
    const context = useOutletContext();
    const isSidebarOpen = context ? context.isSidebarOpen : true;
    const { t } = useTranslation();
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [playingVideos, setPlayingVideos] = useState({});

    const videos = [
        {
            id: 1,
            src: OnboardingVideo,
            title: t('videoGuide.videos.onboarding.title'),
            description: t('videoGuide.videos.onboarding.description')
        },
        {
            id: 2,
            src: AIAssistantVideo,
            title: t('videoGuide.videos.aiAssistant.title'),
            description: t('videoGuide.videos.aiAssistant.description')
        },
        {
            id: 3,
            src: AssistantAgendaVideo,
            title: t('videoGuide.videos.agenda.title'),
            description: t('videoGuide.videos.agenda.description')
        },
        {
            id: 4,
            src: AutomatiseringenVideo,
            title: t('videoGuide.videos.automations.title'),
            description: t('videoGuide.videos.automations.description')
        },
        {
            id: 5,
            src: SkillsVideo,
            title: t('videoGuide.videos.skills.title'),
            description: t('videoGuide.videos.skills.description')
        }
    ];

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
    };

    const handleCloseModal = () => {
        setSelectedVideo(null);
    };

    const handleVideoPlay = (videoId) => {
        setPlayingVideos(prev => ({ ...prev, [videoId]: true }));
    };

    const handleVideoPause = (videoId) => {
        setPlayingVideos(prev => ({ ...prev, [videoId]: false }));
    };

    return (
        <GuideContainer $isSidebarOpen={isSidebarOpen}>
            <Header>
                <Title>{t('videoGuide.title')}</Title>
                <Subtitle>
                    {t('videoGuide.subtitle')}
                </Subtitle>
            </Header>

            <VideoGrid>
                {videos.map((video) => (
                    <VideoCard key={video.id} onClick={() => handleVideoClick(video)}>
                        <VideoWrapper>
                            <video
                                src={video.src}
                                muted
                                loop
                                playsInline
                                onPlay={() => handleVideoPlay(video.id)}
                                onPause={() => handleVideoPause(video.id)}
                                onMouseEnter={(e) => e.target.play()}
                                onMouseLeave={(e) => e.target.pause()}
                            />
                            <PlayOverlay $isPlaying={playingVideos[video.id]} />
                        </VideoWrapper>
                        <VideoInfo>
                            <VideoTitle>{video.title}</VideoTitle>
                            <VideoDescription>{video.description}</VideoDescription>
                        </VideoInfo>
                    </VideoCard>
                ))}
            </VideoGrid>

            {selectedVideo && (
                <ModalOverlay onClick={handleCloseModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={handleCloseModal}>×</CloseButton>
                        <ModalVideo
                            src={selectedVideo.src}
                            controls
                            autoPlay
                            playsInline
                        />
                    </ModalContent>
                </ModalOverlay>
            )}
        </GuideContainer>
    );
}

export default VideoGuide;